import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Globe, Trophy, TrendingUp, MapPin, Share2, X, Download, Copy, Check, ExternalLink, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { FoundingBadge } from "@/components/FoundingBadge";
import { shareToApp, shareViaWebAPI } from "@/hooks/useNativeShare";

// Total countries in the world (UN recognized)
const TOTAL_COUNTRIES = 195;
const TOTAL_CONTINENTS = 7;

// Rank tiers with thresholds and styling - EXPORTED for use in other components
export const RANK_TIERS = [
  { min: 0, max: 2, name: "Dreamer", emoji: "🛵", color: "from-cyan-400 to-cyan-600", bgColor: "bg-cyan-500/10", textColor: "text-cyan-500", solidColor: "bg-cyan-500", borderColor: "border-cyan-500" },
  { min: 2, max: 5, name: "Pathfinder", emoji: "✈️", color: "from-pink-400 to-pink-600", bgColor: "bg-pink-500/10", textColor: "text-pink-500", solidColor: "bg-pink-500", borderColor: "border-pink-500" },
  { min: 5, max: 10, name: "Adventurer", emoji: "🚞", color: "from-yellow-400 to-yellow-600", bgColor: "bg-yellow-500/10", textColor: "text-yellow-500", solidColor: "bg-yellow-500", borderColor: "border-yellow-500" },
  { min: 10, max: 20, name: "Voyager", emoji: "🛳️", color: "from-green-400 to-green-600", bgColor: "bg-green-500/10", textColor: "text-green-500", solidColor: "bg-green-500", borderColor: "border-green-500" },
  { min: 20, max: 35, name: "Trailblazer", emoji: "🚁", color: "from-red-400 to-red-600", bgColor: "bg-red-500/10", textColor: "text-red-500", solidColor: "bg-red-500", borderColor: "border-red-500" },
  { min: 35, max: 60, name: "World Builder", emoji: "🚀", color: "from-blue-400 to-blue-600", bgColor: "bg-blue-500/10", textColor: "text-blue-500", solidColor: "bg-blue-500", borderColor: "border-blue-500" },
  { min: 60, max: 100, name: "Global Legend", emoji: "👑", color: "from-purple-400 to-purple-600", bgColor: "bg-purple-500/10", textColor: "text-purple-500", solidColor: "bg-purple-500", borderColor: "border-purple-500" },
];

// Get rank based on percentage - EXPORTED for use in other components
export const getRank = (percentage) => {
  for (let i = RANK_TIERS.length - 1; i >= 0; i--) {
    if (percentage >= RANK_TIERS[i].min) {
      return {
        ...RANK_TIERS[i],
        nextRank: RANK_TIERS[i + 1] || null,
        progressToNext: RANK_TIERS[i + 1] 
          ? ((percentage - RANK_TIERS[i].min) / (RANK_TIERS[i].max - RANK_TIERS[i].min)) * 100
          : 100
      };
    }
  }
  return { ...RANK_TIERS[0], nextRank: RANK_TIERS[1], progressToNext: 0 };
};

// Continent data
const CONTINENTS = {
  'Africa': { emoji: '🌍', color: '#F2994A', countries: ['DZ', 'AO', 'BJ', 'BW', 'BF', 'BI', 'CM', 'CV', 'CF', 'TD', 'KM', 'CG', 'CD', 'DJ', 'EG', 'GQ', 'ER', 'SZ', 'ET', 'GA', 'GM', 'GH', 'GN', 'GW', 'CI', 'KE', 'LS', 'LR', 'LY', 'MG', 'MW', 'ML', 'MR', 'MU', 'MA', 'MZ', 'NA', 'NE', 'NG', 'RW', 'ST', 'SN', 'SC', 'SL', 'SO', 'ZA', 'SS', 'SD', 'TZ', 'TG', 'TN', 'UG', 'ZM', 'ZW'] },
  'Asia': { emoji: '🌏', color: '#EB5757', countries: ['AF', 'AM', 'AZ', 'BH', 'BD', 'BT', 'BN', 'KH', 'CN', 'CY', 'GE', 'HK', 'IN', 'ID', 'IR', 'IQ', 'IL', 'JP', 'JO', 'KZ', 'KW', 'KG', 'LA', 'LB', 'MY', 'MV', 'MN', 'MM', 'NP', 'KP', 'OM', 'PK', 'PH', 'QA', 'SA', 'SG', 'KR', 'LK', 'SY', 'TW', 'TJ', 'TH', 'TL', 'TR', 'TM', 'AE', 'UZ', 'VN', 'YE'] },
  'Europe': { emoji: '🇪🇺', color: '#2D9CDB', countries: ['AL', 'AD', 'AT', 'BY', 'BE', 'BA', 'BG', 'HR', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT', 'XK', 'LV', 'LI', 'LT', 'LU', 'MT', 'MD', 'MC', 'ME', 'NL', 'MK', 'NO', 'PL', 'PT', 'RO', 'RU', 'SM', 'RS', 'SK', 'SI', 'ES', 'SE', 'CH', 'UA', 'GB', 'VA'] },
  'North America': { emoji: '🌎', color: '#9B51E0', countries: ['AG', 'BS', 'BB', 'BZ', 'CA', 'CR', 'CU', 'DM', 'DO', 'SV', 'GD', 'GT', 'HT', 'HN', 'JM', 'MX', 'NI', 'PA', 'KN', 'LC', 'VC', 'TT', 'US'] },
  'South America': { emoji: '🌎', color: '#27AE60', countries: ['AR', 'BO', 'BR', 'CL', 'CO', 'EC', 'GY', 'PY', 'PE', 'SR', 'UY', 'VE'] },
  'Oceania': { emoji: '🌊', color: '#F2C94C', countries: ['AU', 'FJ', 'KI', 'MH', 'FM', 'NR', 'NZ', 'PW', 'PG', 'WS', 'SB', 'TO', 'TV', 'VU'] },
  'Antarctica': { emoji: '🧊', color: '#56CCF2', countries: ['AQ'] }
};

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const ExplorerRank = ({ onOpenMap }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showContinentBreakdown, setShowContinentBreakdown] = useState(false);
  const [cardImage, setCardImage] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [globalRank, setGlobalRank] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    fetchStats();
    fetchGlobalRank();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/map/visited`);
      const visitedCodes = res.data?.visited?.country_codes || [];
      
      // Calculate continents visited
      const continentsVisited = Object.entries(CONTINENTS).filter(([name, data]) => 
        data.countries.some(code => visitedCodes.includes(code))
      ).length;
      
      setStats({
        countriesVisited: res.data?.stats?.countries_visited || 0,
        worldPercentage: Math.round((res.data?.stats?.world_percentage || 0) * 10) / 10,
        continentsVisited,
        visitedCodes
      });
    } catch (error) {
      console.error("Failed to fetch explorer stats");
      setStats({
        countriesVisited: 0,
        worldPercentage: 0,
        continentsVisited: 0,
        visitedCodes: []
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalRank = async () => {
    try {
      const res = await axios.get(`${API}/map/global-rank`);
      setGlobalRank(res.data);
    } catch (error) {
      console.error("Failed to fetch global rank");
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-2xl p-6 border border-border animate-pulse">
        <div className="h-32 bg-muted rounded-xl" />
      </div>
    );
  }

  const { countriesVisited, worldPercentage, continentsVisited, visitedCodes } = stats;
  const rank = getRank(worldPercentage);
  const continentPercentage = Math.round((continentsVisited / TOTAL_CONTINENTS) * 100);

  // Calculate continent breakdown
  const continentBreakdown = Object.entries(CONTINENTS).map(([name, data]) => {
    const visited = data.countries.filter(code => visitedCodes.includes(code)).length;
    const total = data.countries.length;
    const remaining = total - visited;
    const percentage = Math.round((visited / total) * 100);
    return {
      name,
      emoji: data.emoji,
      color: data.color,
      visited,
      total,
      remaining,
      percentage
    };
  }).sort((a, b) => b.percentage - a.percentage);

  // Generate share caption
  const generateShareText = () => {
    let text = `${rank.emoji} I'm a ${rank.name}!\n\n`;
    text += `🌍 World Explored: ${worldPercentage}%\n`;
    text += `📍 Countries Visited: ${countriesVisited}/${TOTAL_COUNTRIES}\n`;
    text += `🗺️ Continents: ${continentsVisited}/${TOTAL_CONTINENTS}\n\n`;
    
    if (rank.nextRank) {
      text += `Next rank: ${rank.nextRank.emoji} ${rank.nextRank.name}\n\n`;
    }
    
    text += `Track your adventures with @duolyst 🗺️`;
    return text;
  };

  const generateHashtags = () => {
    return '#duolyst #explorer #travel #worldmap #bucketlist #wanderlust #travelgoals #adventure';
  };

  // Capture card as image
  const captureCard = async () => {
    if (!cardRef.current) return;
    
    setCapturing(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        logging: false,
      });
      
      const imageUrl = canvas.toDataURL('image/png');
      setCardImage(imageUrl);
      setShowShareModal(true);
    } catch (error) {
      console.error('Failed to capture card:', error);
      toast.error('Failed to capture image');
      setShowShareModal(true);
    } finally {
      setCapturing(false);
    }
  };

  // Download image
  const downloadImage = () => {
    if (!cardImage) return;
    
    const link = document.createElement('a');
    link.download = `duolyst-${rank.name.toLowerCase()}-rank.png`;
    link.href = cardImage;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Image downloaded!');
  };

  // Copy caption
  const handleCopyCaption = async () => {
    const fullText = `${generateShareText()}\n\n${generateHashtags()}`;
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      toast.success("Caption copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  // Native share
  const handleNativeShare = async () => {
    const caption = `${generateShareText()}\n\n${generateHashtags()}`;
    
    // Try Web Share API with file if supported
    if (navigator.canShare && cardImage) {
      try {
        const response = await fetch(cardImage);
        const blob = await response.blob();
        const file = new File([blob], `duolyst-${rank.name.toLowerCase()}-rank.png`, { type: 'image/png' });
        
        const shareData = {
          title: `I'm a ${rank.name}!`,
          text: caption,
          files: [file]
        };
        
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          toast.success('Shared successfully!');
          return;
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    }
    
    // Fallback: try Web Share without file
    const shared = await shareViaWebAPI(caption, window.location.origin, `I'm a ${rank.name}!`);
    if (!shared) {
      if (cardImage) downloadImage();
      handleCopyCaption();
      toast.info('Image downloaded & caption copied!');
    }
  };

  const handleShareClick = (e) => {
    e.stopPropagation();
    captureCard();
  };

  return (
    <>
      <div 
        ref={cardRef}
        className="bg-card rounded-2xl p-5 border border-border hover:shadow-lg transition-shadow cursor-pointer relative"
        onClick={onOpenMap}
        data-testid="explorer-rank-card"
      >
        {/* Share Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleShareClick}
          disabled={capturing}
          className="absolute top-3 right-3 h-8 w-8 p-0 rounded-full"
          data-testid="share-rank-btn"
        >
          {capturing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Share2 className="w-4 h-4" />
          )}
        </Button>

        {/* Header with Rank */}
        <div className="flex items-center justify-between mb-4 pr-8">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl ${rank.bgColor} flex items-center justify-center text-2xl shadow-lg`}>
              {rank.emoji}
            </div>
            <div>
              <h3 className={`font-extrabold text-xl ${rank.textColor} flex items-center gap-2 drop-shadow-[0_0_10px_currentColor]`}>
                {rank.name}
                {user?.founding_explorer && (
                  <FoundingBadge 
                    size="xs" 
                    founderNumber={user.founding_explorer_number}
                    className="h-5 w-5"
                  />
                )}
              </h3>
              <p className="text-xs text-muted-foreground">Travel Rank</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-foreground">{worldPercentage}%</div>
            <p className="text-xs text-muted-foreground">World Explored</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">Progress to {rank.nextRank?.name || "Max"}</span>
            <span className={rank.textColor}>{Math.round(rank.progressToNext)}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full bg-gradient-to-r ${rank.color} transition-all duration-500`}
              style={{ width: `${rank.progressToNext}%` }}
            />
          </div>
          {rank.nextRank && (
            <p className="text-xs text-muted-foreground mt-1">
              {rank.nextRank.emoji} {Math.round((rank.max - worldPercentage) * 10) / 10}% more to {rank.nextRank.name}
            </p>
          )}
        </div>

        {/* Global Rank */}
        {globalRank && (
          <div className="mb-4 p-3 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl border border-primary/20">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <Trophy className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">
                  {globalRank.ahead_of_percent > 0 
                    ? `You're ahead of ${globalRank.ahead_of_percent}% of explorers`
                    : "Start exploring to climb the ranks!"}
                </p>
                <p className="text-xs text-muted-foreground">
                  Rank #{globalRank.rank} of {globalRank.total_users || '?'} explorers
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 bg-muted/50 rounded-xl">
            <div className="flex items-center justify-center gap-1 mb-1">
              <MapPin className="w-4 h-4 text-primary" />
            </div>
            <div className="text-xl font-bold text-foreground">{countriesVisited}</div>
            <div className="text-xs text-muted-foreground">Countries</div>
          </div>
          <div className="text-center p-3 bg-muted/50 rounded-xl">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Globe className="w-4 h-4 text-secondary" />
            </div>
            <div className="text-xl font-bold text-foreground">{continentsVisited}/7</div>
            <div className="text-xs text-muted-foreground">Continents</div>
          </div>
          <div className="text-center p-3 bg-muted/50 rounded-xl">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Trophy className="w-4 h-4 text-primary" />
            </div>
            <div className="text-xl font-bold text-foreground">{continentPercentage}%</div>
            <div className="text-xs text-muted-foreground">Continent %</div>
          </div>
        </div>

        {/* Continent Breakdown Toggle */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowContinentBreakdown(!showContinentBreakdown);
          }}
          className="w-full mt-4 flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/50 rounded-xl transition-colors"
        >
          <span className="text-sm font-medium text-foreground flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Continent Breakdown
          </span>
          {showContinentBreakdown ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </button>

        {/* Continent Breakdown Content */}
        {showContinentBreakdown && (
          <div className="mt-3 space-y-3 animate-in slide-in-from-top-2 duration-200" onClick={(e) => e.stopPropagation()}>
            {continentBreakdown.map((continent) => (
              <div key={continent.name} className="p-3 bg-muted/30 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{continent.emoji}</span>
                    <span className="font-medium text-foreground text-sm">{continent.name}</span>
                  </div>
                  <span className="text-sm font-bold" style={{ color: continent.color }}>
                    {continent.percentage}%
                  </span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden mb-2">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${continent.percentage}%`,
                      backgroundColor: continent.color
                    }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{continent.visited}/{continent.total} visited</span>
                  {continent.remaining > 0 ? (
                    <span className="text-foreground/70">
                      {continent.remaining} left to complete {continent.name}
                    </span>
                  ) : (
                    <span className="text-green-500 font-medium">✓ Completed!</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tap to view hint */}
        <p className="text-xs text-muted-foreground text-center mt-3 flex items-center justify-center gap-1">
          <TrendingUp className="w-3 h-3" />
          Tap to view your World Map
        </p>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto" onClick={() => setShowShareModal(false)}>
          <div 
            className="bg-card rounded-2xl shadow-2xl p-5 max-w-sm w-full animate-in fade-in zoom-in duration-200 my-auto max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-lg text-foreground flex items-center gap-2">
                <Share2 className="w-5 h-5 text-primary" />
                Share Your Rank
              </h3>
              <button 
                onClick={() => {
                  setShowShareModal(false);
                  setCardImage(null);
                }}
                className="text-muted-foreground hover:text-foreground p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Image Preview */}
            {cardImage ? (
              <div className="mb-4">
                <div className="rounded-xl overflow-hidden border border-border">
                  <img src={cardImage} alt="Your Explorer Rank" className="w-full h-auto" />
                </div>
              </div>
            ) : (
              <div className="mb-4 p-8 bg-muted/50 rounded-xl flex flex-col items-center justify-center">
                <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
              </div>
            )}
            
            {/* Quick Stats */}
            <div className="flex items-center justify-center gap-4 mb-3 text-sm">
              <span className={`font-extrabold text-lg ${rank.textColor} drop-shadow-[0_0_8px_currentColor]`}>{rank.emoji} {rank.name}</span>
              <span className="text-muted-foreground">•</span>
              <span className="text-foreground">{worldPercentage}% explored</span>
            </div>
            
            {/* Caption Preview */}
            <div className="mb-4 p-3 bg-muted/50 rounded-lg max-h-32 overflow-y-auto">
              <p className="text-xs text-foreground whitespace-pre-line">{generateShareText()}</p>
              <p className="text-xs text-primary mt-2">{generateHashtags()}</p>
            </div>
            
            {/* Share Buttons */}
            <div className="space-y-2">
              <Button
                onClick={handleNativeShare}
                disabled={!cardImage}
                className="w-full h-11 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:opacity-90 text-white font-semibold"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share Image + Caption
              </Button>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    if (cardImage) downloadImage();
                    handleCopyCaption();
                    shareToApp("instagram", caption);
                  }}
                  variant="outline"
                  className="flex-1 h-10"
                >
                  Instagram
                </Button>
                
                <Button
                  onClick={() => {
                    if (cardImage) downloadImage();
                    handleCopyCaption();
                    shareToApp("tiktok", caption);
                  }}
                  variant="outline"
                  className="flex-1 h-10"
                >
                  <TikTokIcon className="w-4 h-4 mr-1" />
                  TikTok
                </Button>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={handleCopyCaption}
                  variant="outline"
                  className="flex-1 h-10"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-1 text-green-500" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      Caption
                    </>
                  )}
                </Button>
                
                {cardImage && (
                  <Button
                    onClick={downloadImage}
                    variant="outline"
                    className="flex-1 h-10"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Image
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExplorerRank;
